const API_URL =
  import.meta.env.VITE_API_URL ||
  'https://niftee-orchestrator.jason-a6e.workers.dev';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function getAuthHeader(): string | null {
  // Try session token first (new auth)
  const session = localStorage.getItem('niftee_session');
  if (session) {
    try {
      const { token } = JSON.parse(session);
      if (token) {
        return `Bearer ${token}`;
      }
    } catch {
      // Ignore parse errors
    }
  }

  // Fall back to legacy API key (for backwards compatibility during transition)
  const apiKey = localStorage.getItem('niftee_auth');
  if (apiKey) {
    try {
      const parsed = JSON.parse(apiKey);
      if (parsed.apiKey) {
        return parsed.apiKey; // Return raw key for X-API-Key header
      }
    } catch {
      // Ignore parse errors
    }
  }

  return null;
}

export async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const auth = getAuthHeader();

  // Determine which header to use based on auth format
  const authHeaders: Record<string, string> = {};
  if (auth) {
    if (auth.startsWith('Bearer ')) {
      authHeaders['Authorization'] = auth;
    } else {
      authHeaders['X-API-Key'] = auth;
    }
  }

  // Don't force application/json for FormData (file uploads) — the browser
  // must set multipart/form-data with the correct boundary itself, otherwise
  // the worker's request.formData() can't parse the body (→ 500 on upload).
  const isFormData =
    typeof FormData !== 'undefined' && options.body instanceof FormData;

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...authHeaders,
      ...options.headers,
    },
  });

  if (!response.ok) {
    let errorMessage = 'API request failed';
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorData.message || errorMessage;
    } catch {
      // Use default error message
    }
    throw new ApiError(errorMessage, response.status);
  }

  return response.json();
}

export function apiGet<T>(endpoint: string): Promise<T> {
  return apiRequest<T>(endpoint, { method: 'GET' });
}

export function apiPost<T>(endpoint: string, body?: unknown): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

export function apiPatch<T>(endpoint: string, body?: unknown): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'PATCH',
    body: body ? JSON.stringify(body) : undefined,
  });
}

export function apiPut<T>(endpoint: string, body?: unknown): Promise<T> {
  return apiRequest<T>(endpoint, {
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
}
